import type { PathParameters } from '../../types';

const urls: Readonly<{
	readonly alerts: {
		readonly responders: {
			readonly autoComplete: {
				readonly url: () => '/v1/alerts/responders/auto-complete';
			};
			readonly names: {
				readonly url: () => '/v1/alerts/responders/names';
			};
			readonly suggested: {
				readonly url: () => '/v1/alerts/responders/suggested';
			};
		};
	};
	readonly customers: {
		readonly timezones: {
			readonly url: () => '/v1/customers/timezones';
		};
	};
	readonly schedules: {
		readonly scheduleId: {
			readonly clone: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly disable: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly enable: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly name: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly overrides: {
				readonly alias: {
					readonly url: (pathParameters?: PathParameters<'scheduleId' | 'alias'>) => string;
				};
				readonly bulk: {
					readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
				};
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly rotations: {
				readonly rotationId: {
					readonly name: {
						readonly url: (pathParameters?: PathParameters<'scheduleId' | 'rotationId'>) => string;
					};
					readonly order: {
						readonly url: (pathParameters?: PathParameters<'scheduleId' | 'rotationId'>) => string;
					};
					readonly url: (pathParameters?: PathParameters<'scheduleId' | 'rotationId'>) => string;
				};
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly timeline: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly timezone: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			readonly webCalendarUrl: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
		};
		readonly timeTemplates: {
			readonly rotation: {
				readonly url: () => '/v1/schedules/time-templates/rotation';
			};
		};
		readonly url: () => '/v1/schedules';
		readonly user: {
			readonly webCalendarUrl: {
				readonly url: () => '/v1/schedules/user/web-calendar-url';
			};
		};
	};
	readonly users: {
		readonly contacts: {
			readonly primary: {
				readonly url: () => '/v1/users/contact/primary';
			};
		};
	};
}> = Object.freeze({
	alerts: {
		responders: {
			names: {
				url: (): '/v1/alerts/responders/names' => '/v1/alerts/responders/names',
			},
			autoComplete: {
				url: (): '/v1/alerts/responders/auto-complete' => '/v1/alerts/responders/auto-complete',
			},
			suggested: {
				url: (): '/v1/alerts/responders/suggested' => '/v1/alerts/responders/suggested',
			},
		},
	},
	schedules: {
		url: (): '/v1/schedules' => `/v1/schedules`,
		scheduleId: {
			url: (
				{ scheduleId }: PathParameters<'scheduleId'> = {
					scheduleId: ':scheduleId',
				},
			): string => `/v1/schedules/${scheduleId}`,
			clone: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/clone`,
			},
			name: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/name`,
			},
			timezone: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/timezone`,
			},
			enable: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/enable`,
			},
			disable: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/disable`,
			},
			webCalendarUrl: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/web-calendar-url`,
			},
			timeline: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/timeline`,
			},
			overrides: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/overrides`,
				alias: {
					url: (
						{ scheduleId, alias }: PathParameters<'scheduleId' | 'alias'> = {
							scheduleId: ':scheduleId',
							alias: ':alias',
						},
					): string => `/v1/schedules/${scheduleId}/overrides/${alias}`,
				},
				bulk: {
					url: (
						{ scheduleId }: PathParameters<'scheduleId'> = {
							scheduleId: ':scheduleId',
						},
					): string => `/v1/schedules/${scheduleId}/overrides/bulk`,
				},
			},
			rotations: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/rotations`,
				rotationId: {
					url: (
						{ scheduleId, rotationId }: PathParameters<'scheduleId' | 'rotationId'> = {
							scheduleId: ':scheduleId',
							rotationId: ':rotationId',
						},
					): string => `/v1/schedules/${scheduleId}/rotations/${rotationId}`,
					order: {
						url: (
							{ scheduleId, rotationId }: PathParameters<'scheduleId' | 'rotationId'> = {
								scheduleId: ':scheduleId',
								rotationId: ':rotationId',
							},
						): string => `/v1/schedules/${scheduleId}/rotations/${rotationId}/order`,
					},
					name: {
						url: (
							{ scheduleId, rotationId }: PathParameters<'scheduleId' | 'rotationId'> = {
								scheduleId: ':scheduleId',
								rotationId: ':rotationId',
							},
						): string => `/v1/schedules/${scheduleId}/rotations/${rotationId}/name`,
					},
				},
			},
		},
		user: {
			webCalendarUrl: {
				url: (): '/v1/schedules/user/web-calendar-url' => `/v1/schedules/user/web-calendar-url`,
			},
		},
		timeTemplates: {
			rotation: {
				url: (): '/v1/schedules/time-templates/rotation' => '/v1/schedules/time-templates/rotation',
			},
		},
	},
	customers: {
		timezones: {
			url: (): '/v1/customers/timezones' => '/v1/customers/timezones',
		},
	},
	users: {
		contacts: {
			primary: {
				url: (): '/v1/users/contact/primary' => '/v1/users/contact/primary',
			},
		},
	},
} as const);

export { urls };
